import { useState } from 'react';
import { Doctor, Staff, Room, ScheduleItem, Appointment, Task } from './SchedulerTypes';
import TimeSlotCell from './TimeSlotCell';
import StickyTableWrapper from './StickyTableWrapper';
import MultiItemSelectionModal from './MultiItemSelectionModal';

interface DoctorScheduleTableProps {
  timeSlots: string[];
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
  scheduleItems: ScheduleItem[];
  appointments?: Appointment[];
  tasks?: Task[];
  getAppointmentForScheduleItem?: (scheduleItemId: string) => Appointment | undefined;
  getTaskForScheduleItem?: (scheduleItemId: string) => Task | undefined;
  onCellClick: (time: string, personId: string, isDoctor?: boolean) => void;
  onEditItem: (item: ScheduleItem) => void;
  onBookAppointment?: (schedule: ScheduleItem) => void;
  onViewAppointment?: (appointment: Appointment) => void;
  onAssignTask?: (schedule: ScheduleItem) => void;
  onViewTask?: (task: Task) => void;
  onAddScheduleItem?: (item: Omit<ScheduleItem, 'id'>) => void;
  onDeleteScheduleItem?: (id: string) => void;
  currentDate?: string;
}

const DoctorScheduleTable = ({
  timeSlots,
  doctors,
  staff,
  rooms,
  scheduleItems,
  appointments = [],
  tasks = [],
  getAppointmentForScheduleItem = () => undefined,
  getTaskForScheduleItem = () => undefined,
  onCellClick,
  onEditItem,
  onBookAppointment = () => {},
  onViewAppointment = () => {},
  onAssignTask = () => {},
  onViewTask = () => {},
  onAddScheduleItem,
  onDeleteScheduleItem,
  currentDate,
}: DoctorScheduleTableProps) => {
  // Sort doctors by company and priority: ENDO (1), TDS (2), SSD (3), TCD (4), EM (5), TCEP (6), others (7), then by priority (0=highest), then by name
  const sortDoctorsByCompanyAndPriority = (a: Doctor, b: Doctor) => {
    const getCompanyPriority = (company?: string) => {
      switch (company?.toUpperCase()) {
        case 'ENDO': return 1;
        case 'TDS': return 2;
        case 'SSD': return 3;
        case 'TCD': return 4;
        case 'EM': return 5;
        case 'TCEP': return 6;
        default: return 7;
      }
    };

    const companyPriorityA = getCompanyPriority(a.company);
    const companyPriorityB = getCompanyPriority(b.company);
    
    // Primary sort by company
    if (companyPriorityA !== companyPriorityB) {
      return companyPriorityA - companyPriorityB;
    }
    
    // Secondary sort by priority within same company (0 is highest priority)
    const priorityA = a.priority || 0;
    const priorityB = b.priority || 0;
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    
    // Tertiary sort by name within same company and priority
    return a.name.localeCompare(b.name);
  };

  const sortedDoctors = [...doctors].sort(sortDoctorsByCompanyAndPriority);

  // Multi-item selection modal state
  const [multiSelectItems, setMultiSelectItems] = useState<ScheduleItem[]>([]);
  const [multiSelectTime, setMultiSelectTime] = useState<string>('');
  const [isMultiSelectOpen, setIsMultiSelectOpen] = useState(false);

  const handleShowMultiItemSelection = (items: ScheduleItem[], time: string) => {
    setMultiSelectItems(items);
    setMultiSelectTime(time);
    setIsMultiSelectOpen(true);
  };

  const handleSelectItem = (item: ScheduleItem) => {
    onEditItem(item);
  };

  const renderDoctorHeader = (doctor: Doctor) => {
    return (
      <th key={doctor.id} className="h-12 px-4 text-center align-middle font-medium text-muted-foreground text-xs">
        <div className="text-sm font-medium">{doctor.short_name || doctor.name}</div>
        <div className="text-xs text-muted-foreground">{doctor.specialization}</div>
      </th>
    );
  };

  const renderDoctorCell = (time: string, doctorId: string) => {
    return (
      <TimeSlotCell
        key={`${time}-${doctorId}`}
        time={time}
        personId={doctorId}
        isDoctor={true}
        scheduleItems={scheduleItems || []}
        appointments={appointments}
        tasks={tasks}
        doctors={doctors}
        staff={staff}
        rooms={rooms}
        getAppointmentForScheduleItem={getAppointmentForScheduleItem}
        getTaskForScheduleItem={getTaskForScheduleItem}
        onCellClick={onCellClick}
        onEditItem={onEditItem}
        onBookAppointment={onBookAppointment}
        onViewAppointment={onViewAppointment}
        onAssignTask={onAssignTask}
        onViewTask={onViewTask}
        onAddScheduleItem={onAddScheduleItem}
        onDeleteScheduleItem={onDeleteScheduleItem}
        currentDate={currentDate}
        onShowMultiItemSelection={handleShowMultiItemSelection}
      />
    );
  };

  return (
    <>
    <StickyTableWrapper>
      <thead className="[&_tr]:border-b">
        <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
            <th className="h-12 px-4 text-center align-middle font-medium text-muted-foreground w-16">TIME</th>
            {sortedDoctors.map(doctor => renderDoctorHeader(doctor))}
          </tr>
        </thead>
        <tbody className="[&_tr:last-child]:border-0">
          {timeSlots.map(time => (
            <tr key={time} className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
              <td className="p-4 align-middle font-mono text-xs text-center bg-muted/30">
                {time}
              </td>
              {sortedDoctors.map(doctor => renderDoctorCell(time, doctor.id))}
            </tr>
          ))}
      </tbody>
    </StickyTableWrapper>

    {/* Multi-item selection modal */}
    <MultiItemSelectionModal
      isOpen={isMultiSelectOpen}
      onClose={() => setIsMultiSelectOpen(false)}
      items={multiSelectItems}
      onSelectItem={handleSelectItem}
      time={multiSelectTime}
      doctors={doctors}
      staff={staff}
      rooms={rooms}
    />
    </>
  );
};

export default DoctorScheduleTable;
